// Setup Advisor (04 Advisor and Cart; parts/Advisor Chat and Cart; sprint plan 4D). The intake page sends one desk photo,
// the desk size, a budget and a use to the campaign Worker through the app proxy; the plan page shows the plan the Worker
// checked, with this store's own product rows (ns-advisor-plan-item) and a cart of its plain-stock items; the chat drawer
// asks the same Advisor; Will it fit compares a product's footprint with the shopper's desk. Every word from the Advisor
// is written as text, never as markup.
export const ENDPOINTS = { status: '/apps/9space/advisor/status', plan: '/apps/9space/advisor/plan', chat: '/apps/9space/advisor/chat' };
export const USES = ['work', 'gaming', 'creative', 'study', 'streaming'];
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const SEND_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_ORIGINAL = 20 * 1024 * 1024;
const MAX_SEND = 5 * 1024 * 1024;
const HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;
const KEYS = { plan: 'ns:advisor-plan', deskPlan: 'ns:desk-plan', deskSize: 'ns:desk-size', chat: 'ns:advisor-chat' };
const ERRORS = ['not_ready', 'busy', 'limited', 'timeout', 'too_large', 'invalid', 'unusable', 'unavailable'];

const round1 = value => Math.round(value * 10) / 10;
const number = value => (typeof value === 'string' && value.trim() ? Number(value) : value);

// The same conversions as the Worker (services/campaigns/advisor.js), so the page and the plan agree on every fit.
export function toCm(value, unit) {
  const n = number(value);
  if (typeof n !== 'number' || !Number.isFinite(n)) return NaN;
  if (unit === 'cm') return round1(n);
  if (unit === 'mm') return Math.round(n) / 10;
  if (unit === 'in') return Math.round(Math.round(n * 254) / 10) / 10;
  return NaN;
}

export function checkDesk(desk) {
  const width = toCm(desk?.width, desk?.unit);
  const depth = toCm(desk?.depth, desk?.unit);
  if (!(width >= 20 && width <= 400) || !(depth >= 20 && depth <= 200)) return { ok: false };
  return { ok: true, desk: { width_cm: width, depth_cm: depth } };
}

export function parseFootprint(raw) {
  let value = raw;
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try { value = JSON.parse(value); } catch { return null; }
  }
  let width; let depth; let unit;
  if (value && typeof value === 'object') {
    ({ width, depth, unit } = value);
  } else if (typeof value === 'string') {
    const match = /^\s*(\d+(?:\.\d+)?)\s*[x×*]\s*(\d+(?:\.\d+)?)\s*(cm|mm|in|inch|inches|")\s*$/i.exec(value);
    if (!match) return null;
    width = Number(match[1]);
    depth = Number(match[2]);
    unit = match[3].toLowerCase();
    if (unit === '"' || unit.startsWith('inch')) unit = 'in';
  } else {
    return null;
  }
  const w = toCm(width, unit);
  const d = toCm(depth, unit);
  if (!(w >= 1 && w <= 400) || !(d >= 1 && d <= 400)) return null;
  return { width_cm: w, depth_cm: d };
}

// Width along the desk's width, depth along its depth: a product is never turned sideways to make it fit.
export function fitCheck(footprint, desk) {
  const width = round1(desk.width_cm - footprint.width_cm);
  const depth = round1(desk.depth_cm - footprint.depth_cm);
  return { fits: width >= 0 && depth >= 0, width_cm: width, depth_cm: depth };
}

export const formatLength = (cm, unit) => (unit === 'in' ? `${round1(cm / 2.54)} in` : `${round1(cm)} cm`);
const fill = (template, values) => String(template ?? '').replace(/\{(\w+)\}/g, (_, name) => values[name] ?? '');

export function fitText(result, unit, strings) {
  const values = { width: formatLength(Math.abs(result.width_cm), unit), depth: formatLength(Math.abs(result.depth_cm), unit) };
  if (result.fits) return fill(strings.fits, values);
  if (result.width_cm < 0 && result.depth_cm < 0) return fill(strings.both, values);
  return fill(result.width_cm < 0 ? strings.wide : strings.deep, values);
}

// Long edge at most 1600 px; a smaller photo keeps its size.
export function downscaleSize(width, height, max = 1600) {
  const long = Math.max(width, height);
  if (!(long > max)) return { width, height };
  const scale = max / long;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

// ?photo=none (the landing's "No photo? Answer 4 questions"): the intake drops the photo and asks the Advisor in words.
export const photoMode = search => (new URLSearchParams(search ?? '').get('photo') === 'none' ? 'none' : 'photo');

export function checkIntake({ file, width, depth, unit, budget, use, notes }, { photo = true } = {}) {
  const errors = [];
  if (photo && (!file || !PHOTO_TYPES.includes(file.type) || !(file.size > 0 && file.size <= MAX_ORIGINAL))) errors.push('photo');
  if (!checkDesk({ width, depth, unit }).ok) errors.push('desk');
  const dollars = number(budget);
  if (!(dollars >= 10 && dollars <= 20000)) errors.push('budget');
  if (!USES.includes(use)) errors.push('use');
  if (String(notes ?? '').trim().length > 300) errors.push('notes');
  return { ok: errors.length === 0, errors };
}

// The intake's optional chip questions (How many screens?, Which vibe?) travel in the note the Worker already takes, ahead
// of the shopper's own words; when both would run past the note's 300 characters, the shopper's words go alone.
export const NOTES_MAX = 300;
export function foldNotes(notes, extras = []) {
  const note = String(notes ?? '').trim();
  const lead = extras.map(extra => String(extra ?? '').trim()).filter(Boolean).join(' ');
  if (!lead) return note;
  const both = note ? `${lead} ${note}` : lead;
  return both.length <= NOTES_MAX ? both : note;
}
const extraAnswers = form => [...form.querySelectorAll('[data-ns-intake-extra]')].map(set => {
  const picked = set.querySelector('input:checked');
  return picked ? fill(set.dataset.fold, { answer: picked.closest('label')?.textContent.trim() || picked.value }) : '';
});

export function planPayload({ image, width, depth, unit, budget, use, notes, handles, goal }) {
  const body = { image, desk: { width: number(width), depth: number(depth), unit }, budget: number(budget), use, notes: String(notes ?? '').trim(), context: { handles } };
  if (goal) body.goal = goal;
  return body;
}

// The home picker's card rides on the intake link (?goal=lighting|new_desk|finish) and goes to the Worker as the goal.
const GOALS = ['lighting', 'new_desk', 'finish'];
export function goalFromSearch(search) {
  const goal = new URLSearchParams(search ?? '').get('goal');
  return GOALS.includes(goal) ? goal : null;
}

// Without a photo the plan endpoint cannot run (the Worker needs one), so the answers go to the chat endpoint as one
// question in the shopper's words: the section's template with the desk, budget, use (its label) and note filled in.
export function noPhotoQuestion({ width, depth, unit, budget, notes }, { template, use }) {
  const note = String(notes ?? '').trim();
  return fill(template, { width: number(width), depth: number(depth), unit, budget: number(budget), use: use ?? '', notes: note }).replace(/\s{2,}/g, ' ').trim();
}

// ?product=<handle> (Why these?, Will it fit), ?owns=<a,b> (the Day-30 email) and ?products=<a,b> (the gallery's Rebuild
// for my desk) become the Advisor's context.
export function contextFromSearch(search) {
  const params = new URLSearchParams(search);
  const list = [params.get('product') ?? '', ...(params.get('owns') ?? '').split(','), ...(params.get('products') ?? '').split(',')].map(item => item.trim()).filter(item => HANDLE.test(item));
  return [...new Set(list)].slice(0, 6);
}

// The Worker answers with a code; the page has its own words for each. No app proxy yet (404) is "not available yet".
export function errorKey(status, body) {
  if (ERRORS.includes(body?.error)) return body.error;
  if (status === 404 || status === 503) return 'not_ready';
  if (status === 429) return 'limited';
  if (status === 504) return 'timeout';
  if (status === 413) return 'too_large';
  return 'unavailable';
}

const read = (storage, key) => { try { return JSON.parse(storage?.getItem(key) ?? 'null'); } catch { return null; } };
const write = (storage, key, value) => { try { storage.setItem(key, JSON.stringify(value)); return true; } catch { return false; } };
const validPlan = plan => typeof plan?.summary === 'string' && Array.isArray(plan.fits) && plan.fits.every(fit => typeof fit?.handle === 'string') && Array.isArray(plan.sequence) && Array.isArray(plan.missing);

// The Advisor's four steps (07 Interactive Prototype: Photo, 4 questions, Your plan, Follow-up); those before the current
// one are done.
export const stepStates = (current, total = 4) => Array.from({ length: total }, (_, i) => (i + 1 < current ? 'done' : i + 1 === current ? 'current' : 'todo'));

// "N of 4 answered" beside the button: the desk size (both sides), the budget, the use and the note.
export function answeredCount({ width, depth, budget, use, notes } = {}) {
  const filled = value => String(value ?? '').trim() !== '';
  return [filled(width) && filled(depth), filled(budget), filled(use), filled(notes)].filter(Boolean).length;
}

// The shopper's answers travel with the plan in this tab (never the photo), for the plan's header line; the use is its label.
const answersOf = ({ width, depth, unit, budget, useLabel, use }) => ({ width: number(width), depth: number(depth), unit, budget: number(budget), use: String(useLabel ?? use ?? '') });
export const writePlan = (storage, plan, now, answers) => write(storage, KEYS.plan, answers ? { createdAt: now, plan, answers: answersOf(answers) } : { createdAt: now, plan });
export function readPlan(storage) {
  const plan = read(storage, KEYS.plan)?.plan;
  return validPlan(plan) ? plan : null;
}
export function readPlanAnswers(storage) {
  const answers = read(storage, KEYS.plan)?.answers;
  if (!answers || !checkDesk(answers).ok) return null;
  const dollars = number(answers.budget);
  return { width: number(answers.width), depth: number(answers.depth), unit: answers.unit, budget: dollars >= 10 && dollars <= 20000 ? dollars : null, use: typeof answers.use === 'string' ? answers.use.trim().slice(0, 40) : '' };
}
// "140 × 70 cm desk · Work · $600 budget", from the page's own words for the desk and the budget.
export function planMeta(answers, strings) {
  if (!answers) return '';
  const desk = fill(strings.desk, { width: answers.width, depth: answers.depth, unit: answers.unit });
  const budget = answers.budget === null || answers.budget === undefined ? '' : fill(strings.budget, { budget: answers.budget });
  return [desk, answers.use, budget].map(part => String(part ?? '').trim()).filter(Boolean).join(' · ');
}

// Your cart from this plan: the rows ns-advisor-plan-item marked as plain stock (a variant and a price in cents), each
// variant once, with their total. Only these go in the cart from the plan page; the rest open their product page.
export function cartSummary(rows) {
  const seen = new Set();
  const lines = [];
  for (const row of rows) {
    const id = Number(row?.variant);
    const price = Number(row?.price);
    if (!Number.isInteger(id) || id <= 0 || row?.price === '' || !Number.isFinite(price) || price < 0 || seen.has(id)) continue;
    seen.add(id);
    lines.push({ id, price, title: String(row.title ?? '').trim() });
  }
  return { count: lines.length, total: lines.reduce((sum, line) => sum + line.price, 0), lines };
}

// Like Shopify's money_without_trailing_zeros: $228, $65.50.
export function formatMoney(cents, currency) {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '';
  const options = { style: 'currency', minimumFractionDigits: value % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 };
  try {
    return new Intl.NumberFormat('en-US', { ...options, currency: currency || 'USD' }).format(value / 100);
  } catch {
    return new Intl.NumberFormat('en-US', { ...options, currency: 'USD' }).format(value / 100);
  }
}

// One compact row per plan product, from this store's own section (its state, its price, plain stock or not).
export const planItemUrl = handle => `/products/${encodeURIComponent(handle)}?section_id=ns-advisor-plan-item`;

// What 5B's "My desk plan" page reads from ns:desk-plan.
export const deskPlanRecord = (plan, now) => ({ savedAt: new Date(now).toISOString(), summary: plan.summary, items: plan.fits.map(fit => ({
  handle: fit.handle, why: fit.why, title: fit.title || fit.handle, url: `/products/${encodeURIComponent(fit.handle)}`, note: fit.why || '',
})) });
export const saveDeskPlan = (storage, plan, now) => write(storage, KEYS.deskPlan, deskPlanRecord(plan, now));

export function saveDeskSize(storage, { width, depth, unit }) {
  if (!checkDesk({ width, depth, unit }).ok) return false;
  return write(storage, KEYS.deskSize, { width: number(width), depth: number(depth), unit });
}
export function readDeskSize(storage) {
  const size = read(storage, KEYS.deskSize);
  return size && checkDesk(size).ok ? { width: size.width, depth: size.depth, unit: size.unit } : null;
}

export function groupSequence(plan) {
  const byHandle = new Map(plan.fits.map(fit => [fit.handle, fit]));
  const pick = state => plan.sequence.filter(item => item.availability === state && byHandle.has(item.handle)).map(item => byHandle.get(item.handle));
  return { now: pick('in_stock'), later: pick('pre_order') };
}

export function capHistory(history, max = 10) {
  const kept = history.slice(-max);
  while (kept.length && kept[0].role !== 'user') kept.shift();
  return kept;
}
export const chatPayload = (history, handles) => ({ messages: capHistory(history).map(({ role, text }) => ({ role, text })), context: { handles } });
export const saveChat = (storage, history) => write(storage, KEYS.chat, capHistory(history));
export function readChat(storage) {
  const list = read(storage, KEYS.chat);
  return Array.isArray(list) ? list.filter(item => ['user', 'assistant'].includes(item?.role) && typeof item.text === 'string') : [];
}

// Shopify Inbox, when its embed is on the page: its API if it has one, else the button inside its element.
export function inboxOpener(win, doc) {
  if (typeof win?.ShopifyChat?.open === 'function') return () => win.ShopifyChat.open();
  const button = doc?.querySelector?.('inbox-online-store-chat')?.shadowRoot?.querySelector?.('button');
  return button ? () => button.click() : null;
}

// Plans we've built: the design's budget tabs, from each plan's real total in cents.
export function budgetBand(cents) {
  const value = Number(cents);
  if (cents === null || cents === undefined || cents === '' || !Number.isFinite(value)) return '';
  return value < 50000 ? 'under500' : value < 100000 ? '500to1k' : 'over1k';
}

const productUrl = handle => `/products/${encodeURIComponent(handle)}`;
const element = (doc, tag, className, text) => {
  const node = doc.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
};

// One plan product: a slot the store's card replaces (a plain link until it loads), the reason, and the fit when known.
export function planItem(doc, fit, strings) {
  const item = element(doc, 'li', 'ns-advisor-plan__item');
  item.dataset.handle = fit.handle;
  const slot = element(doc, 'div', 'ns-advisor-plan__card');
  slot.dataset.nsPlanCard = '';
  const link = element(doc, 'a', 'ns-advisor-plan__link', fit.title || fit.handle);
  link.href = productUrl(fit.handle);
  slot.append(link);
  item.append(slot);
  if (fit.why) item.append(element(doc, 'p', 'ns-advisor-plan__why', fit.why));
  if (fit.fits_desk === true || fit.fits_desk === false) item.append(element(doc, 'span', `ns-advisor-plan__fit ns-advisor-plan__fit--${fit.fits_desk ? 'yes' : 'no'}`, fit.fits_desk ? strings.fits : strings.no_fit));
  return item;
}

export const missingItem = (doc, text) => element(doc, 'li', 'ns-advisor-plan__missing-item', text);

// A line of "Your cart from this plan" (name and price as text), and the hidden field the cart form posts for it.
export function cartLine(doc, line, currency) {
  const item = element(doc, 'li', 'ns-advisor-cart__line');
  item.append(element(doc, 'span', 'ns-advisor-cart__name', line.title), element(doc, 'span', 'ns-advisor-cart__price', formatMoney(line.price, currency)));
  return item;
}
export function cartInput(doc, id) {
  const input = doc.createElement('input');
  input.type = 'hidden';
  input.name = 'items[][id]';
  input.value = String(id);
  return input;
}

export function chatBubble(doc, message) {
  const bubble = element(doc, 'div', `ns-advisor-chat__msg ns-advisor-chat__msg--${message.role === 'user' ? 'user' : 'assistant'}`);
  bubble.append(element(doc, 'p', 'ns-advisor-chat__text', message.text));
  if (message.products?.length) {
    const list = element(doc, 'ul', 'ns-advisor-chat__products');
    for (const product of message.products) {
      const row = element(doc, 'li', 'ns-advisor-chat__product');
      const link = element(doc, 'a', 'ns-advisor-chat__product-link', product.title || product.handle);
      link.href = productUrl(product.handle);
      row.append(link);
      if (product.why) row.append(element(doc, 'span', 'ns-advisor-chat__product-why', product.why));
      list.append(row);
    }
    bubble.append(list);
  }
  return bubble;
}

// ---- Browser wiring ----

const local = () => { try { return window.localStorage; } catch { return null; } };
const session = () => { try { return window.sessionStorage; } catch { return null; } };

async function request(url, body) {
  try {
    const response = await fetch(url, body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {});
    const json = await response.json().catch(() => null);
    return { status: response.status, json };
  } catch {
    return { status: 0, json: null };
  }
}

let readiness = null;
const advisorReady = () => readiness ?? (readiness = request(ENDPOINTS.status).then(({ status, json }) => status === 200 && json?.ready === true));

const blobToBase64 = blob => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

// Scaled to 1600 px and re-encoded as JPEG on a white ground (a transparent PNG stays readable). A browser that cannot
// decode the photo sends it as it is, when it is a type and size the Worker takes.
async function preparePhoto(file) {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const size = downscaleSize(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    context.fillStyle = '#fff';
    context.fillRect(0, 0, size.width, size.height);
    context.drawImage(bitmap, 0, 0, size.width, size.height);
    bitmap.close?.();
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (blob && blob.size <= MAX_SEND) return { media_type: 'image/jpeg', data: await blobToBase64(blob) };
  } catch { /* fall back to the original below */ }
  if (SEND_TYPES.includes(file.type) && file.size <= MAX_SEND) return { media_type: file.type, data: await blobToBase64(file) };
  return null;
}

// The node carries one data attribute per error code (data-not-ready, data-too-large…), read as dataset.notReady.
function showError(node, key) {
  if (!node) return;
  node.textContent = node.dataset[key.replace(/_(\w)/g, (_, char) => char.toUpperCase())] || node.dataset.unavailable || '';
  node.hidden = !node.textContent;
}

// The use is a row of radio chips; its words are the checked chip's label.
const useLabel = form => form.querySelector('[name="use"]:checked')?.closest('label')?.textContent?.trim() || form.elements.use.value;

function paintSteps(list, current) {
  if (!list) return;
  const states = stepStates(current, list.children.length);
  [...list.children].forEach((step, i) => {
    step.dataset.state = states[i];
    if (states[i] === 'current') step.setAttribute('aria-current', 'step');
    else step.removeAttribute('aria-current');
  });
}

// No photo: the answers go to the chat endpoint as one question, and the reply shows on the page (and in the drawer's history).
async function askWithoutPhoto(page, form, values, error) {
  const use = useLabel(form);
  const text = noPhotoQuestion(values, { template: page.querySelector('[data-ns-intake-words]')?.dataset.question, use }).slice(0, 1000);
  const history = [{ role: 'user', text }];
  const { status, json } = await request(ENDPOINTS.chat, chatPayload(history, contextFromSearch(window.location.search)));
  if (!(status === 200 && json?.ok === true && typeof json.reply === 'string')) { showError(error, errorKey(status, json)); return; }
  const message = { role: 'assistant', text: json.reply, products: Array.isArray(json.products) ? json.products : [] };
  saveChat(session(), [...history, message]);
  saveDeskSize(local(), values);
  const answer = page.querySelector('[data-ns-intake-answer]');
  answer.querySelector('[data-ns-intake-reply]').replaceChildren(chatBubble(document, message));
  answer.hidden = false;
  answer.querySelector('h2')?.setAttribute('tabindex', '-1');
  answer.querySelector('h2')?.focus();
}

function startIntake(page) {
  const form = page.querySelector('[data-ns-intake-form]');
  const noPhoto = photoMode(window.location.search) === 'none';
  const steps = page.querySelector('[data-ns-advisor-steps]');
  if (noPhoto) {
    const photo = page.querySelector('[data-ns-intake-photo]');
    if (photo) photo.hidden = true;
    // Without a photo the answer comes back here from the chat, not as a plan, so the steps (and the intro about the
    // photo) would mislead.
    if (steps) steps.hidden = true;
    const intro = page.querySelector('.ns-advisor-intake__text');
    if (intro) intro.hidden = true;
    form.elements.photo.required = false;
    form.elements.photo.disabled = true;
    const words = page.querySelector('[data-ns-intake-words]')?.dataset ?? {};
    const title = page.querySelector('.ns-advisor-intake__title');
    if (title && words.noPhotoTitle) title.textContent = words.noPhotoTitle;
    const submitLabel = form.querySelector('[data-ns-submit-label]') ?? form.querySelector('[type="submit"]');
    if (submitLabel && words.noPhotoSubmit) submitLabel.textContent = words.noPhotoSubmit;
  }
  const unavailable = page.querySelector('[data-ns-advisor-unavailable]');
  const error = page.querySelector('[data-ns-intake-error]');
  const status = page.querySelector('[data-ns-intake-status]');
  const preview = page.querySelector('[data-ns-intake-preview]');
  const tile = page.querySelector('[data-ns-photo-tile]');
  const fileChip = page.querySelector('[data-ns-intake-file]');
  const count = page.querySelector('[data-ns-intake-count]');
  const saved = readDeskSize(local());
  if (saved) {
    form.elements.width.value = saved.width;
    form.elements.depth.value = saved.depth;
    for (const radio of form.querySelectorAll('[name="unit"]')) radio.checked = radio.value === saved.unit;
  }
  const answered = () => {
    if (!count) return;
    const values = { width: form.elements.width.value, depth: form.elements.depth.value, budget: form.elements.budget.value, use: form.elements.use.value, notes: form.elements.notes.value };
    count.textContent = fill(count.dataset.template, { count: answeredCount(values) });
  };
  answered();
  form.addEventListener('input', answered);
  form.addEventListener('change', answered);
  advisorReady().then(ready => { form.hidden = !ready; unavailable.hidden = ready; page.setAttribute('aria-busy', 'false'); });
  form.elements.photo.addEventListener('change', () => {
    const file = form.elements.photo.files?.[0];
    // The tile shows the photo, its name and Replace; the stepper moves on to the questions.
    if (tile) tile.toggleAttribute('data-chosen', Boolean(file));
    if (fileChip) {
      fileChip.hidden = !file;
      const name = fileChip.querySelector('[data-ns-intake-file-name]');
      if (name) name.textContent = file ? file.name : '';
    }
    paintSteps(steps, file ? 2 : 1);
    if (!preview) return;
    if (preview.dataset.url) URL.revokeObjectURL(preview.dataset.url);
    preview.hidden = !file;
    if (file) { preview.dataset.url = URL.createObjectURL(file); preview.src = preview.dataset.url; }
  });
  let busy = false;
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy) return;
    const values = { file: form.elements.photo.files?.[0] ?? null, width: form.elements.width.value, depth: form.elements.depth.value, unit: form.elements.unit.value, budget: form.elements.budget.value, use: form.elements.use.value, notes: foldNotes(form.elements.notes.value, extraAnswers(form)) };
    const checked = checkIntake(values, { photo: !noPhoto });
    for (const node of form.querySelectorAll('[data-ns-field-error]')) node.hidden = !checked.errors.includes(node.dataset.nsFieldError);
    error.hidden = true;
    if (!checked.ok) { form.querySelector(`[data-ns-field="${checked.errors[0]}"]`)?.focus(); return; }
    busy = true;
    const submit = form.querySelector('[type="submit"]');
    submit.disabled = true;
    status.hidden = false;
    page.setAttribute('aria-busy', 'true');
    try {
      if (noPhoto) { await askWithoutPhoto(page, form, values, error); return; }
      const image = await preparePhoto(values.file);
      if (!image) { showError(error, 'too_large'); return; }
      const { status: code, json } = await request(ENDPOINTS.plan, planPayload({ ...values, image, handles: contextFromSearch(window.location.search), goal: goalFromSearch(window.location.search) }));
      if (code === 200 && json?.ok === true && writePlan(session(), json.plan, Date.now(), { ...values, useLabel: useLabel(form) })) {
        saveDeskSize(local(), values);
        window.location.assign(page.dataset.planUrl);
        return;
      }
      // A 400 names the fields the Worker refused (its "image" is the form's "photo").
      const refused = Array.isArray(json?.fields) ? json.fields.map(field => (field === 'image' ? 'photo' : field)) : [];
      for (const node of form.querySelectorAll('[data-ns-field-error]')) if (refused.includes(node.dataset.nsFieldError)) node.hidden = false;
      showError(error, code === 200 ? 'unavailable' : errorKey(code, json));
    } catch {
      showError(error, 'unavailable');
    } finally {
      busy = false;
      submit.disabled = false;
      status.hidden = true;
      page.setAttribute('aria-busy', 'false');
    }
  });
}

// Your cart from this plan: the Fix today rows that can go straight in the cart, their total on every Add button, and one
// hidden items[][id] each in the form (ns-cart.js takes the add over, as with Shop the look).
function paintCart(page, plan) {
  const form = page.querySelector('[data-ns-plan-cart-form]');
  const rows = [...page.querySelectorAll('[data-ns-plan-group="now"] [data-ns-plan-row][data-variant]')].map(row => ({ variant: row.dataset.variant, price: row.dataset.price, title: row.querySelector('.ns-plan-row__title')?.textContent }));
  const cart = cartSummary(rows);
  const currency = form?.dataset.currency;
  const total = formatMoney(cart.total, currency);
  page.querySelector('[data-ns-plan-cart-lines]')?.replaceChildren(...cart.lines.map(line => cartLine(document, line, currency)));
  page.querySelector('[data-ns-plan-cart-inputs]')?.replaceChildren(...cart.lines.map(line => cartInput(document, line.id)));
  for (const node of page.querySelectorAll('[data-ns-plan-total]')) node.textContent = total;
  for (const node of page.querySelectorAll('[data-ns-plan-count]')) node.textContent = fill(cart.count === 1 ? node.dataset.one : node.dataset.other, { count: cart.count });
  for (const button of page.querySelectorAll('[data-ns-plan-cart-add]')) button.textContent = fill(button.dataset.template, { total });
  for (const node of page.querySelectorAll('[data-ns-plan-cart-only]')) node.hidden = cart.count === 0;
  const none = page.querySelector('[data-ns-plan-cart-none]');
  if (none) none.hidden = cart.count > 0 || plan.fits.length === 0;
}

async function startPlan(page) {
  const plan = readPlan(session());
  const body = page.querySelector('[data-ns-plan-body]');
  page.querySelector('[data-ns-plan-empty]').hidden = Boolean(plan);
  body.hidden = !plan;
  for (const node of page.querySelectorAll('[data-ns-plan-only]')) node.hidden = !plan;
  const side = page.querySelector('[data-ns-plan-side]');
  if (side) side.hidden = !plan?.fits.length;
  if (!plan) return;
  const strings = page.dataset;
  const meta = page.querySelector('[data-ns-plan-meta]');
  if (meta) {
    meta.textContent = planMeta(readPlanAnswers(session()), { desk: meta.dataset.desk, budget: meta.dataset.budget });
    meta.hidden = !meta.textContent;
  }
  // Ask about this plan: the chat drawer opens with the plan's products as its context.
  const ask = page.querySelector('[data-ns-advisor-ask]');
  if (ask) ask.dataset.handles = plan.fits.map(fit => fit.handle).filter(handle => HANDLE.test(handle)).slice(0, 6).join(',');
  page.querySelector('[data-ns-plan-summary]').textContent = plan.summary;
  const groups = groupSequence(plan);
  for (const [name, fits] of Object.entries(groups)) {
    const group = page.querySelector(`[data-ns-plan-group="${name}"]`);
    const list = group.querySelector('[data-ns-plan-list]');
    list.replaceChildren(...fits.map(fit => planItem(document, fit, { fits: strings.fits, no_fit: strings.noFit })));
    group.hidden = !fits.length;
  }
  page.querySelector('[data-ns-plan-none]').hidden = plan.fits.length > 0;
  page.querySelector('[data-ns-plan-save]').hidden = plan.fits.length === 0;
  const missing = page.querySelector('[data-ns-plan-missing]');
  missing.querySelector('ul').replaceChildren(...plan.missing.map(text => missingItem(document, text)));
  missing.hidden = !plan.missing.length;
  // The layout already loads ns-watch.js; importing its exact URL reuses that module instead of running it twice. Each
  // product comes back as a compact row from ns-advisor-plan-item (07 prototype: photo, name, price, stock).
  try {
    const watch = await import(page.dataset.watchModule);
    page.setAttribute('aria-busy', 'true');
    for (const card of await watch.loadCards(plan.fits.map(fit => fit.handle), url => fetch(url), planItemUrl)) {
      if (!card.html) continue;
      const slot = page.querySelector(`.ns-advisor-plan__item[data-handle="${CSS.escape(card.handle)}"] [data-ns-plan-card]`);
      if (slot) slot.innerHTML = watch.unwrapSection(card.html);
    }
  } catch { /* the plain product links stay */ } finally {
    paintCart(page, plan);
    page.setAttribute('aria-busy', 'false');
  }
}

function startFit(block) {
  const footprint = parseFootprint(block.dataset.footprint);
  if (!footprint) return;
  block.hidden = false;
  const form = block.querySelector('[data-ns-fit-form]');
  const result = block.querySelector('[data-ns-fit-result]');
  const size = block.querySelector('[data-ns-fit-size]');
  const unitOf = () => form.elements.unit.value;
  const showSize = () => { size.textContent = fill(size.dataset.template, { width: formatLength(footprint.width_cm, unitOf()), depth: formatLength(footprint.depth_cm, unitOf()) }); };
  const saved = readDeskSize(local());
  if (saved) {
    form.elements.width.value = saved.width;
    form.elements.depth.value = saved.depth;
    for (const radio of form.querySelectorAll('[name="unit"]')) radio.checked = radio.value === saved.unit;
  }
  showSize();
  form.addEventListener('change', showSize);
  form.addEventListener('submit', event => {
    event.preventDefault();
    const values = { width: form.elements.width.value, depth: form.elements.depth.value, unit: unitOf() };
    const desk = checkDesk(values);
    result.hidden = false;
    if (!desk.ok) { result.dataset.state = 'error'; result.textContent = result.dataset.invalid; return; }
    saveDeskSize(local(), values);
    const check = fitCheck(footprint, desk.desk);
    result.dataset.state = check.fits ? 'yes' : 'no';
    result.textContent = fitText(check, values.unit, result.dataset);
  });
}

function startChat(dialog) {
  const log = dialog.querySelector('[data-ns-chat-log]');
  const form = dialog.querySelector('[data-ns-chat-form]');
  const unavailable = dialog.querySelector('[data-ns-advisor-unavailable]');
  const thinking = dialog.querySelector('[data-ns-chat-thinking]');
  const error = dialog.querySelector('[data-ns-chat-error]');
  const person = dialog.querySelector('[data-ns-chat-person]');
  let history = readChat(session());
  let handles = [];
  let busy = false;
  const paint = () => { log.replaceChildren(...history.map(message => chatBubble(document, message))); log.scrollTop = log.scrollHeight; };
  const send = async text => {
    if (busy || !text.trim()) return;
    busy = true;
    error.hidden = true;
    history = [...history, { role: 'user', text: text.trim().slice(0, 1000) }];
    paint();
    thinking.hidden = false;
    form.querySelector('[type="submit"]').disabled = true;
    const { status, json } = await request(ENDPOINTS.chat, chatPayload(history, handles));
    thinking.hidden = true;
    form.querySelector('[type="submit"]').disabled = false;
    busy = false;
    if (status === 200 && json?.ok === true && typeof json.reply === 'string') {
      history = capHistory([...history, { role: 'assistant', text: json.reply, products: Array.isArray(json.products) ? json.products : [] }]);
      saveChat(session(), history);
      paint();
      return;
    }
    // Not answered: the message goes back into the box to send again.
    history = history.slice(0, -1);
    paint();
    if (!form.elements.message.value) form.elements.message.value = text.trim();
    showError(error, errorKey(status, json));
    if (errorKey(status, json) === 'not_ready') { form.hidden = true; unavailable.hidden = false; }
  };
  const open = async ({ question = '', context = [] } = {}) => {
    if (context.length) handles = context;
    paint();
    // Inbox loads on its own schedule, so whether a person can be reached is checked each time the drawer opens.
    if (person) person.hidden = !inboxOpener(window, document) && !person.getAttribute('href');
    if (!dialog.open) dialog.showModal();
    const ready = await advisorReady();
    form.hidden = !ready;
    unavailable.hidden = ready;
    if (ready && question) send(question);
    else if (ready) form.elements.message.focus();
  };
  form.addEventListener('submit', event => {
    event.preventDefault();
    const text = form.elements.message.value;
    form.elements.message.value = '';
    send(text);
  });
  person?.addEventListener('click', event => {
    const openInbox = inboxOpener(window, document);
    if (!openInbox) return;
    event.preventDefault();
    dialog.close();
    openInbox();
  });
  dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
  dialog.querySelector('[data-ns-dialog-close]')?.addEventListener('click', () => dialog.close());
  return open;
}

// Prefer a human?: Chat now opens Shopify Inbox when its embed is on the page (it loads late, so it is looked for again).
function startHuman(section) {
  const chat = section.querySelector('[data-ns-human-chat]');
  if (!chat) return;
  const look = () => {
    if (!inboxOpener(window, document)) return false;
    chat.hidden = false;
    section.hidden = false;
    return true;
  };
  if (!look()) {
    window.addEventListener('load', look, { once: true });
    setTimeout(look, 4000);
  }
  chat.addEventListener('click', event => {
    const openInbox = inboxOpener(window, document);
    if (!openInbox) return;
    event.preventDefault();
    openInbox();
  });
}

// Plans we've built: budget tabs, shown only when the plans fall in more than one band.
function startPlans(section) {
  const tabs = section.querySelector('[data-ns-plan-tabs]');
  const cases = [...section.querySelectorAll('.ns-advisor-case')].map(node => ({ node, band: budgetBand(node.dataset.total) }));
  const bands = new Set(cases.map(item => item.band).filter(Boolean));
  if (!tabs || bands.size < 2) return;
  for (const button of tabs.querySelectorAll('[data-ns-plan-band]')) if (button.dataset.nsPlanBand && !bands.has(button.dataset.nsPlanBand)) button.hidden = true;
  tabs.hidden = false;
  tabs.addEventListener('click', event => {
    const button = event.target instanceof Element ? event.target.closest('[data-ns-plan-band]') : null;
    if (!button) return;
    const band = button.dataset.nsPlanBand;
    for (const tab of tabs.querySelectorAll('[data-ns-plan-band]')) tab.setAttribute('aria-pressed', String(tab === button));
    for (const item of cases) item.node.hidden = Boolean(band) && item.band !== band;
  });
}

if (typeof document !== 'undefined') {
  const start = () => {
    for (const section of document.querySelectorAll('[data-ns-human]')) startHuman(section);
    for (const section of document.querySelectorAll('[data-ns-plans]')) startPlans(section);
    for (const page of document.querySelectorAll('[data-ns-advisor-intake]')) startIntake(page);
    for (const page of document.querySelectorAll('[data-ns-advisor-plan]')) startPlan(page);
    for (const block of document.querySelectorAll('[data-ns-fit]')) startFit(block);
    for (const save of document.querySelectorAll('[data-ns-plan-save]')) {
      save.addEventListener('click', () => {
        const plan = readPlan(session());
        const status = save.closest('[data-ns-advisor-plan]')?.querySelector('[data-ns-plan-status]');
        const done = plan && saveDeskPlan(local(), plan, Date.now());
        if (status) status.textContent = done ? save.dataset.saved : save.dataset.failed;
      });
    }
    const dialog = document.getElementById('NsAdvisorChat');
    const open = dialog ? startChat(dialog) : null;
    if (!open) return;
    document.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target : null;
      const launcher = target?.closest('[data-ns-advisor-open]');
      const ask = target?.closest('[data-ns-advisor-ask]');
      if (!launcher && !ask) return;
      event.preventDefault();
      if (ask) open({ question: ask.dataset.question, context: (ask.dataset.handles || '').split(',').filter(handle => HANDLE.test(handle)).slice(0, 6) });
      else open();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}
